import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { SubSink } from "subsink";
import { AuthService } from "../../../../../../auth/auth.service";
import { BASE_API_URL } from "../../../../../../core/environment.tokens";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { GridColumnDefinition } from "../../../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../../../shared/grid/models/grid-configuration.model";
import { GridRequest } from "../../../../../../shared/grid/models/grid-request.model";
import { ArrayHelper } from "../../../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../../../utilities/contracts/string-helper";
import { AuditPageEntryService } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entry.service";
import { AuditDataSharingService } from "../../../services/audit-data-sharing.service";
import { AuditPackageService } from "../../../services/audit-package.service";
import { AuditQueryGridRiskService } from "../audit-query-grid-risk/audit-query-grid-risk.service";
import { AuditPackageGenerateRiskItem } from "./audit-package-generate-risk-item.model";
import { AuditPackageGenerateRiskRequest } from "./audit-package-generate-risk-request.model";
import { AuditPackageGenerateRiskService } from "./audit-package-generate-risk.service";

@Component({
  selector: "app-audit-package-generate-risk",
  templateUrl: "./audit-package-generate-risk.component.html",
  styleUrls: ["./audit-package-generate-risk.component.scss"],
})

export class AuditPackageGenerateRiskComponent implements OnInit, OnDestroy  {
  auditPackageGridConfiguration = new GridConfiguration();
  auditPackageGridSearchRequest: GridRequest;
  refreshGrid = new EventEmitter<GridConfiguration>(true);
  auditPackageGridData: AuditPackageGenerateRiskItem[];
  auditPackageGridSelection: AuditPackageGenerateRiskItem;
  packageCreationInitiated = false;
  projectId: number;
  isFileNameEntryVisible = false;
  filename: string;
  showNotificationForAuditGeneration = false;
  private sink = new SubSink();
  gridChase: any = [];
  complianceChangeStatus: string;
  private currentMemberId: number;
  private isDeletingPackageItem = false;
  @ViewChild("actionItem", { static: true }) actionItem: TemplateRef<any>;

  constructor(private auditPackageGenerateRiskService: AuditPackageGenerateRiskService,
              private auditQueryGridRiskService: AuditQueryGridRiskService,
              private readonly authService: AuthService,
              private changeDetector: ChangeDetectorRef,
              @Inject(BASE_API_URL) private readonly baseApiUrl: string,
              private messagingService: MessagingService,
              private auditPageEntryService: AuditPageEntryService,
              private auditPackageService: AuditPackageService,
              private auditDataSharingService: AuditDataSharingService
  ) { }

  ngOnInit() {
    this.createGrid();

    this.auditPackageGenerateRiskService.refreshAuditPackageGrid(null);
    this.sink.add(
      this.auditPackageGenerateRiskService.projectId
        .subscribe(data => {
          this.projectId = data;
          this.loadGrid();
        }),

      this.auditQueryGridRiskService.auditGridFilter
        .subscribe(filters => {
          this.currentMemberId = filters?.enrollee;
        }),

      this.auditPackageGenerateRiskService.autoDeleteUserAuditEntry
        .subscribe(data => {
          const auditEntry = new AuditPackageGenerateRiskItem({
            auditPackageItemId: data,
          });
          this.deletePackageItem(auditEntry);
        })
    );

    if (this.projectId) {
      this.loadGrid();
    }
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  private createGrid(): void {
    this.auditPackageGridConfiguration = new GridConfiguration({
      columns: [
        new GridColumnDefinition({ field: "chaseId", header: "Chase ID" }),
        new GridColumnDefinition({ field: "icdCode", header: "ICD" }),
        new GridColumnDefinition({ field: "hccs", header: "HCCs" }),
        new GridColumnDefinition({ field: "vrc", header: "VRC" }),
        new GridColumnDefinition({ field: "pageRanges", header: "Page Number(s)" }),
        new GridColumnDefinition({ header: "", template: this.actionItem, isSortableColumn: false }),
      ],
      pageSize: 10,
      pageSizeOptions: [5, 10, 15, 20],
      selectionMode: "none",
      showMenu: false,
    });

    this.auditPackageGridConfiguration.showExportAction = false;
    this.auditPackageGridConfiguration.showActionColumn = false;
  }

  private loadGrid(): void {
    this.packageCreationInitiated = true;
    this.loadRiskAuditPackageItems();

    setTimeout(() => {
      if (!this.auditPackageGridData || this.auditPackageGridData.length === 0) {
        this.loadRiskAuditPackageItems();
      }
    },         1000);
  }

  private loadRiskAuditPackageItems(): void {
    const existingAuditPackages = this.auditDataSharingService.getAuditPackages();

    if (existingAuditPackages && existingAuditPackages.length > 0) {
      const auditPackageId = existingAuditPackages[0].auditPackageID;
      const existingPageEntries = this.auditDataSharingService.getPageEntries(auditPackageId);

      if (existingPageEntries && existingPageEntries.length > 0) {
        this.transformAndDisplayPageEntries(existingPageEntries);
        return;
      } else {
        this.waitForPageEntries(auditPackageId);
        return;
      }
    }

    this.sink.add(
      this.auditDataSharingService.getAuditPackagesObservable().subscribe(auditPackages => {
        if (auditPackages && auditPackages.length > 0) {
          const auditPackageId = auditPackages[0].auditPackageID;
          const existingPageEntries = this.auditDataSharingService.getPageEntries(auditPackageId);
          if (existingPageEntries && existingPageEntries.length > 0) {
            this.transformAndDisplayPageEntries(existingPageEntries);
          } else {
            this.loadPageEntriesForRisk(auditPackageId, this.projectId);
          }
        } else {
          this.auditPackageGridData = [];
          this.changeDetector.markForCheck();
        }
      })
    );

    this.sink.add(
      this.auditDataSharingService.getPageEntriesObservable().subscribe(pageEntriesMap => {
        if (pageEntriesMap && pageEntriesMap.size > 0) {
          const firstAuditPackageId = Array.from(pageEntriesMap.keys())[0];
          const pageEntries = pageEntriesMap.get(firstAuditPackageId);
          if (pageEntries && pageEntries.length > 0) {
            this.transformAndDisplayPageEntries(pageEntries);
          }
        }
      })
    );
  }

  private loadPageEntriesForRisk(auditPackageId: number, projectId?: number): void {
    const cachedPageEntries = this.auditDataSharingService.getPageEntries(auditPackageId);
    if (cachedPageEntries && cachedPageEntries.length > 0) {
      const filteredPageEntries = cachedPageEntries.filter(pageEntries => {
        return pageEntries.riskPageData;
      });
      this.auditPackageGridData = this.transformPageEntriesToGridData(filteredPageEntries, projectId);
      this.changeDetector.markForCheck();
      return;
    }

    this.waitForPageEntries(auditPackageId);
  }

  private waitForPageEntries(auditPackageId: number): void {
    this.sink.add(
      this.auditDataSharingService.getPageEntriesObservable().subscribe(pageEntriesMap => {
        const pageEntries = pageEntriesMap.get(auditPackageId);
        if (pageEntries && pageEntries.length > 0) {
          this.transformAndDisplayPageEntries(pageEntries);
        }
      })
    );
  }

  private transformAndDisplayPageEntries(pageEntries: any[]): void {
    const filteredPageEntries = pageEntries.filter(pageEntry => pageEntry.riskPageData && pageEntry.status === "selected");
    this.auditPackageGridData = this.transformPageEntriesToGridData(filteredPageEntries, this.projectId);
    this.changeDetector.markForCheck();
  }

  private extractPageRanges(riskData: any): string {
    const pages = riskData.pages || {};
    const selectedPages = pages.selectedPages || [];
    const pageRanges: string[] = [];
    selectedPages.forEach(selectedPage => {
      const pageStart = selectedPage.startPage || null;
      const pageEnd = selectedPage.endPage || null;

      if (pageStart !== null && pageEnd !== null) {
        if (pageStart === pageEnd) {
          pageRanges.push(`${pageStart}`);
        } else {
          pageRanges.push(`${pageStart}-${pageEnd}`);
        }
      } else if (pageStart !== null) {
        pageRanges.push(`${pageStart}`);
      } else if (pageEnd !== null) {
        pageRanges.push(`${pageEnd}`);
      }
    });
    return pageRanges.join(", ");
  }

  private transformPageEntriesToGridData(pageEntriesList: any[], projectId?: number): AuditPackageGenerateRiskItem[] {
    const effectiveProjectId = projectId || this.projectId;
    return pageEntriesList
      .filter(pageEntries => pageEntries.riskPageData && pageEntries.status === "selected")
      .map(pageEntries => {
        const riskData = pageEntries.riskPageData;
        return new AuditPackageGenerateRiskItem({
          auditPackageItemId: pageEntries.auditPackageItemId,
          auditPackageId: pageEntries.auditPackageId,
          chaseId: riskData.chaseId || pageEntries.chaseId,
          projectId: effectiveProjectId,
          measureCode: "",
          numeratorCode: "",
          pageRanges: this.extractPageRanges(riskData),
          auditReadyUserId: null,
          chaseComplianceCode: "",
          icdCode: riskData.icd?.toUpperCase(),
          hccs: riskData.hcc?.toUpperCase(),
          vrc: riskData.vrc?.toUpperCase(),
          userRank: riskData.userRank,
        });
      });
  }

  gridData($event: AuditPackageGenerateRiskItem[]): void {
    this.auditPackageGridData = $event;
  }

  get auditPackageDataAvailable(): boolean {
    return ArrayHelper.isAvailable(this.auditPackageGridData);
  }

  get displayPackageGridText(): boolean {
    return !this.packageCreationInitiated;
  }

  refreshAuditPackageData(): void {
    this.loadRiskAuditPackageItems();
  }

  deletePackageItem(data: AuditPackageGenerateRiskItem): void {
    if (this.isDeletingPackageItem) {
      return;
    }

    if (NumberHelper.isGreaterThan(Number(data.auditPackageItemId), 0)) {
      this.isDeletingPackageItem = true;

      this.auditPackageGenerateRiskService.deleteAuditPackageItem(Number(data.auditPackageItemId)).subscribe(response => {
          this.messagingService.showToast("Package Item deleted successfully.", SeverityType.SUCCESS);

          try {
            this.auditDataSharingService.clearAllData();
            this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.projectId);
            this.auditQueryGridRiskService.reloadAuditGridFromState();
            this.changeDetector.markForCheck();
          } catch (error) {

          }

          setTimeout(() => {
            this.isDeletingPackageItem = false;
          },         100);
        },                                                                                                   error => {
          this.messagingService.showToast("Failed to delete Package Item.", SeverityType.ERROR);

          setTimeout(() => {
            this.isDeletingPackageItem = false;
          },         100);
        }
      );
    }
  }


  private deletePackageItemIds(data: any): void {
    if (ArrayHelper.isAvailable(data)) {
      this.auditPackageGenerateRiskService.deleteAuditPackageItemIds(data).subscribe(
        () => {
          this.messagingService.showToast("Package Item removed successfully.", SeverityType.SUCCESS);
          this.auditDataSharingService.clearAllData();
          this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.projectId);
          this.changeDetector.markForCheck();
        },
        () => {
          this.messagingService.showToast("Failed to remove Package Item.", SeverityType.ERROR);
          this.changeDetector.markForCheck();
        });
    }
  }

  promptForFileName(): void {
    this.filename = "";

    if (NumberHelper.isAvailable(this.projectId)) {
      this.isFileNameEntryVisible = true;
    }
  }

  get fileNameAvailable(): boolean {
    return StringHelper.isAvailable(this.filename);
  }

  createPackage(): void {
    const projectIds = [];
    projectIds.push(this.projectId);
    const packageItemIds = [];
    const auditPackageIds = [];

    this.auditPackageGridData.forEach(data => {
      packageItemIds.push(Number(data.auditPackageItemId));
      auditPackageIds.push(Number(data.auditPackageId));
    });

    const auditPackageId = this.auditPackageGridData.length > 0 ?
      this.auditPackageGridData[0].auditPackageId : 0;

    const auditPackageRequest = new AuditPackageGenerateRiskRequest({
      projectIds,
      auditPackageItemIds: packageItemIds,
      auditPackageIds,
      packageName: this.filename,
      memberId: this.currentMemberId || 0,
      auditPackageId,
    });

    this.bindGridChaseWithCompliance();
    const selectedChaseId: any = [];
    this.auditPackageGridData.map(x => selectedChaseId.push(x.chaseId));
    const chaseList = {chaseList : selectedChaseId};

    if (ArrayHelper.isAvailable(projectIds)) {

      this.auditPackageGenerateRiskService.chaseListForComplianceCode(chaseList).subscribe(
        chaseItems => {

          const sortedGridChase = this.gridChase.slice().sort((a, b) => a.chaseId - b.chaseId);
          const sortedChaseItems = chaseItems.slice().sort((a, b) => a.chaseId - b.chaseId)
            .reduce((acc, current) => {
              const chase = acc.find(item => item.chaseId === current.chaseId);
              if (!chase) { acc.push(current); }
              return acc;
            },      []);

          const changedComplainceList = this.checkComplianceChanges(sortedGridChase, sortedChaseItems);
          this.gridChase = [];

          if (ArrayHelper.isAvailable(changedComplainceList)) {

            const packageItemId: any = [];
            let ncToCmrCount = 0;
            let cmrToNcCount = 0;

            sortedChaseItems.forEach(sortChase => {

              const gridChase = sortedGridChase.find(item => item.chaseId === sortChase.chaseId);
              if (sortChase) {
                if (gridChase.chaseComplianceCode === "C/MR" && sortChase.chaseComplianceCode !== "C/MR") {
                  cmrToNcCount = cmrToNcCount + 1;
                  packageItemId.push(Number(gridChase.auditPackageItemIds));
                }
                if (gridChase.chaseComplianceCode === "NC" && sortChase.chaseComplianceCode !== "NC") {
                   ncToCmrCount = ncToCmrCount + 1;
                   packageItemId.push(Number(gridChase.auditPackageItemIds));
                }
              }
            });

            if (sortedChaseItems.length !== (cmrToNcCount + ncToCmrCount) && (cmrToNcCount !== 0 || ncToCmrCount !== 0)) {
              this.showNotificationForAuditGeneration = true;
              this.complianceChangeStatus = "Chase(s) have been removed from the Audit Packet as compliance has been changed";
            } else if (sortedChaseItems.length === (cmrToNcCount + ncToCmrCount) && (cmrToNcCount !== 0 || ncToCmrCount !== 0)) {
              this.showNotificationForAuditGeneration = true;
              this.complianceChangeStatus = "No Audit Packet will be created as compliance of all Chase(s) have changed";
            }

            this.deletePackageItemIds(packageItemId);
            auditPackageRequest.auditPackageItemIds = auditPackageRequest.auditPackageItemIds.filter(itemId => !packageItemId.includes(Number(itemId)));
          }
          if (ArrayHelper.isAvailable(auditPackageRequest.auditPackageItemIds)) {
            this.auditPackageGenerateRiskService.generatePackage(auditPackageRequest).subscribe(
              data => {
                if (data) {
                  this.messagingService.showToast("Items Queued For Packaging.", SeverityType.SUCCESS);
                  this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.projectId);
                  this.auditQueryGridRiskService.reloadAuditGridFromState();
                  this.changeDetector.markForCheck();
                }},
              () => {
                this.messagingService.showToast("Failed to generate Package.", SeverityType.ERROR);
                this.changeDetector.markForCheck();
            });
          }

      },
        () => {
        this.messagingService.showToast("Didn't get Compliance Code", SeverityType.ERROR);
        this.changeDetector.markForCheck();
      }

    );

    }

    this.isFileNameEntryVisible = false;
    this.changeDetector.markForCheck();

  }

  private bindGridChaseWithCompliance(): void {
    this.auditPackageGridData.forEach(element => {
        this.gridChase.push({
          chaseId : element.chaseId,
          chaseComplianceCode : element.chaseComplianceCode,
          auditPackageItemIds : element.auditPackageItemId,
        });
    });
  }

  private checkComplianceChanges(sortedGridChase, sortedChaseItems): any {
    return sortedGridChase.filter((element, index) =>
      sortedChaseItems[index]?.chaseId === element.chaseId &&
      sortedChaseItems[index].chaseComplianceCode !== element.chaseComplianceCode
    );
  }


  onConfirmPackageCreation(): void {
    this.promptForFileName();
  }

  closePackageWarning(): void {
    this.showNotificationForAuditGeneration = false;
  }

}
